/**
 * Feature Flags Utility
 *
 * Centralized feature flag management for gradual rollouts and A/B testing.
 * Supports environment variables and dynamic configuration.
 */
import type { ImageProvider } from './image-provider';

export interface FeatureFlags {
  usePipelineLabIntegration: boolean;
  pipelineLabRolloutPercentage: number;
  enableQAValidation: boolean;
  maxQAAttempts: number;
  useSimpleImageGeneration: boolean;
  simpleImageGenerationRolloutPercentage: number;
  useAltStoryLLM: boolean;
  altStoryLLMRolloutPercentage: number;
  useErsV2: boolean;
  ersV2RolloutPercentage: number;
  usePagePromptV2: boolean;
  pagePromptV2RolloutPercentage: number;
  useTwoStageImage: boolean;
  useTwoStageCover: boolean;
  twoStageMeasure: boolean;
  subscriptionsEnabled: boolean;
  merchEnabled: boolean;
  hardcoverEnabled: boolean;
  extraCopiesEnabled: boolean;
  comicsEnabled: boolean;
  pictureBookComicEngine: boolean;
  comicPrintEnabled: boolean;
  comicSquareEnabled: boolean;
  comicFastPreview: boolean;
  comicStoryboardV2: boolean;
  comicPageQa: boolean;
  comicCoverCanon: boolean;
  comicCodeLettering: boolean;
  comicLetteringFixup: boolean;
  fastPathWizard: boolean;
  fastPathWizardRolloutPercentage: number;
  redoGuaranteeEnabled: boolean;
}

/**
 * Get all feature flags from environment variables
 */
export function getFeatureFlags(): FeatureFlags {
  return {
    // Pipeline Lab Integration (default: false for safety)
    usePipelineLabIntegration: process.env.USE_PIPELINE_LAB === 'true',

    // Rollout percentage (0-100, default: 0)
    pipelineLabRolloutPercentage: parseInt(process.env.PIPELINE_LAB_ROLLOUT_PERCENTAGE || '0', 10),

    // QA validation (default: true when Pipeline Lab is enabled)
    enableQAValidation: process.env.ENABLE_QA_VALIDATION !== 'false',

    // Max QA attempts (default: 2)
    maxQAAttempts: parseInt(process.env.MAX_QA_ATTEMPTS || '2', 10),

    // Simple Image Generation (default: false for safety)
    useSimpleImageGeneration: process.env.USE_SIMPLE_IMAGE_GENERATION === 'true',

    // Simple Image Generation rollout percentage (0-100, default: 0)
    simpleImageGenerationRolloutPercentage: parseInt(process.env.SIMPLE_IMAGE_GENERATION_ROLLOUT_PERCENTAGE || '0', 10),

    // Alternate story-LLM experiment (default: false for safety)
    useAltStoryLLM: process.env.USE_ALT_STORY_LLM === 'true',

    // Alternate story-LLM rollout percentage (0-100, default: 0)
    altStoryLLMRolloutPercentage: parseInt(process.env.ALT_STORY_LLM_ROLLOUT_PERCENTAGE || '0', 10),

    // ERS V2 experiment (default: false for safety)
    useErsV2: process.env.USE_ERS_V2 === 'true',

    // ERS V2 rollout percentage (0-100, default: 0)
    ersV2RolloutPercentage: parseInt(process.env.ERS_V2_ROLLOUT_PERCENTAGE || '0', 10),

    // Page Prompt V2 experiment (default: false for safety)
    usePagePromptV2: process.env.USE_PAGE_PROMPT_V2 === 'true',

    // Page Prompt V2 rollout percentage (0-100, default: 0)
    pagePromptV2RolloutPercentage: parseInt(process.env.PAGE_PROMPT_V2_ROLLOUT_PERCENTAGE || '0', 10),

    // Two-stage inner-page pipeline (gpt-image-2 -> nano restyle). Binary on/off, no rollout %.
    useTwoStageImage: process.env.USE_TWOSTAGE_IMAGE === 'true',

    // Two-stage COVER pipeline (gpt-image-2 -> nano restyle), separate from the inner-page flag.
    // Binary on/off, no rollout %. Default OFF -> prod cover behavior unchanged until flipped.
    useTwoStageCover: process.env.USE_TWOSTAGE_COVER === 'true',

    // Validation-only per-page vision QA logging for the two-stage path.
    twoStageMeasure: process.env.TWOSTAGE_MEASURE === 'true',

    // Subscription purchasing. DEFAULT OFF (kill switch, not a rollout experiment).
    // Subscriptions are sold but deliver nothing today: credits are granted by the Stripe
    // webhook and never consumed (SubscriptionService.useCredit has no live caller, and book
    // creation performs no balance check), and no plan benefit — print_discount_percent,
    // priority_processing — is applied anywhere in the codebase. Selling would take money for
    // no delivered functionality. Flip to 'true' only once entitlements are actually enforced.
    subscriptionsEnabled: process.env.SUBSCRIPTIONS_ENABLED === 'true',

    // Merchandise (cover prints on mugs, shirts, posters...). DEFAULT OFF (kill switch).
    // Hidden while the owner focuses on books; the code stays so it can return. Server-only:
    // client components read it via useSiteFlags() (context/SiteFlagsContext), which the
    // root layout fills from this value at request time.
    merchEnabled: process.env.MERCH_ENABLED === 'true',

    // 8x8 hardcover photobook (docs/superpowers/plans/2026-10-08-hardcover-8x8.md). DEFAULT OFF.
    // Gates pricing, checkout and the public copy; orders already placed are fulfilled regardless.
    // Server-only: client components read it via useSiteFlags().
    hardcoverEnabled: process.env.HARDCOVER_ENABLED === 'true',

    // Extra copies and multi-item orders (docs/superpowers/plans/2026-10-17-extra-copies.md). DEFAULT OFF.
    // Server-only: client components read it via useSiteFlags().
    extraCopiesEnabled: process.env.EXTRA_COPIES_ENABLED === 'true',

    // Comic (manga) books. DEFAULT OFF; server-only, clients read it via useSiteFlags().
    comicsEnabled: process.env.COMICS_ENABLED === 'true',
    pictureBookComicEngine: process.env.PICTURE_BOOK_COMIC_ENGINE === 'true',

    // Printed comics (A5 saddle-stitched, docs/superpowers/specs/2026-10-21-printed-comics-design.md). DEFAULT OFF.
    // Gates new comic print orders (and the print-ready add-on) server-side; staff test-bypass orders pass with it off.
    // Paid comic prints already in flight still print with it off (the print worker has no flag check).
    // Server-only: client components read it via useSiteFlags().comicPrint.
    comicPrintEnabled: process.env.COMIC_PRINT_ENABLED === 'true',

    // Square 24-page comics (8x8 hardcover; Phase B). DEFAULT OFF. Kill switch for NEW square books only:
    // existing ones stay purchasable and the worker has no flag check. Staff test-bypass users can still create them.
    // Server-only: client components read it via useSiteFlags().comicSquare.
    comicSquareEnabled: process.env.COMIC_SQUARE_ENABLED === 'true',

    // Fast comic preview (Phase B): the preview is title + logline + cover only; the page script is written afterwards.
    // DEFAULT OFF. 24-page books are always fast; a retry follows the stored script (pitch or full), never this flag.
    comicFastPreview: process.env.COMIC_FAST_PREVIEW === 'true',

    // Comic storyboarding V2 (spine + story lints + one repair + reader caps). Stamped on the book at creation; later
    // steps read the stamp, never this flag. DEFAULT OFF.
    comicStoryboardV2: process.env.COMIC_STORYBOARD_V2 === 'true',

    // Per-page comic likeness check + one redraw for clear mismatches. DEFAULT ON (owner request); COMIC_PAGE_QA=false = kill switch.
    comicPageQa: process.env.COMIC_PAGE_QA !== 'false',

    // Cover costume check after the preview cover is drawn: one redraw for clear mismatches, then the registry
    // costume text is reconciled to the kept cover. DEFAULT ON; COMIC_COVER_CANON=false = kill switch.
    comicCoverCanon: process.env.COMIC_COVER_CANON !== 'false',

    // Comic text lettered by code (node-canvas) instead of drawn by the image model. DEFAULT OFF: the 2026-10-20
    // real-art spike found it worse than model lettering. Read only when a book is created. The cover's stray-text
    // check runs only when COMIC_COVER_CANON is on.
    comicCodeLettering: process.env.COMIC_CODE_LETTERING === 'true',

    // Lettering check-and-edit: each comic page and the cover are checked for lettering faults (wrong or garbled text,
    // a tail at the wrong speaker) and the bad ones edited once or twice. DEFAULT ON; COMIC_LETTERING_FIXUP=false = kill switch.
    comicLetteringFixup: process.env.COMIC_LETTERING_FIXUP !== 'false',

    // Onboarding fast path A/B (docs/superpowers/plans/2026-10-08-onboarding-fast-path.md). DEFAULT OFF.
    // Rollout % = share of visitors (by session id) who get the fast path; 50 = even A/B.
    fastPathWizard: process.env.FAST_PATH_WIZARD === 'true',
    fastPathWizardRolloutPercentage: parseInt(process.env.FAST_PATH_WIZARD_ROLLOUT_PERCENTAGE || '50', 10),

    // Redo guarantee copy + redo links (docs/superpowers/plans/2026-10-08-conversion-trust.md, Task C4). DEFAULT OFF.
    // Off = refunds page, terms, FAQ, checkout and the redo links keep the old "no refunds" wording and show no redo link.
    // Flip only after the legal review (C5) and the redo_requests migration is applied on prod.
    redoGuaranteeEnabled: process.env.REDO_GUARANTEE_ENABLED === 'true',
  };
}

/**
 * Check if Pipeline Lab should be used for this request
 * Uses rollout percentage for gradual deployment
 */
export function shouldUsePipelineLab(bookId?: string): boolean {
  const flags = getFeatureFlags();

  // If feature is completely disabled, return false
  if (!flags.usePipelineLabIntegration) {
    return false;
  }

  // If 100% rollout, always use it
  if (flags.pipelineLabRolloutPercentage >= 100) {
    return true;
  }

  // If 0% rollout, never use it
  if (flags.pipelineLabRolloutPercentage <= 0) {
    return false;
  }

  // Use bookId hash for consistent assignment (same book always gets same treatment)
  if (bookId) {
    const hash = simpleHash(bookId);
    const bucket = hash % 100;
    return bucket < flags.pipelineLabRolloutPercentage;
  }

  // Fallback to random if no bookId (shouldn't happen, but be safe)
  return Math.random() * 100 < flags.pipelineLabRolloutPercentage;
}

/**
 * Check if Simple Image Generation should be used for this request
 * Uses rollout percentage for gradual deployment
 */
export function shouldUseSimpleImageGeneration(bookId?: string): boolean {
  const flags = getFeatureFlags();

  // If feature is completely disabled, return false
  if (!flags.useSimpleImageGeneration) {
    return false;
  }

  // If 100% rollout, always use it
  if (flags.simpleImageGenerationRolloutPercentage >= 100) {
    return true;
  }

  // If 0% rollout, never use it
  if (flags.simpleImageGenerationRolloutPercentage <= 0) {
    return false;
  }

  // Use bookId hash for consistent assignment (same book always gets same treatment)
  if (bookId) {
    const hash = simpleHash(bookId);
    const bucket = hash % 100;
    return bucket < flags.simpleImageGenerationRolloutPercentage;
  }

  // Fallback to random if no bookId (shouldn't happen, but be safe)
  return Math.random() * 100 < flags.simpleImageGenerationRolloutPercentage;
}

/**
 * Check if the alternate story-LLM (OpenAI-compatible, e.g. OpenRouter/Gemma)
 * should be used for story-text generation for this request.
 * Deterministic per bookId so the story-text and (later) visual-registry calls
 * for the same book resolve to the same provider.
 */
export function shouldUseAltStoryLLM(bookId?: string): boolean {
  const flags = getFeatureFlags();

  if (!flags.useAltStoryLLM) {
    return false;
  }
  if (flags.altStoryLLMRolloutPercentage >= 100) {
    return true;
  }
  if (flags.altStoryLLMRolloutPercentage <= 0) {
    return false;
  }
  if (bookId) {
    const hash = simpleHash(bookId);
    const bucket = hash % 100;
    return bucket < flags.altStoryLLMRolloutPercentage;
  }
  return Math.random() * 100 < flags.altStoryLLMRolloutPercentage;
}

/**
 * Check if ERS V2 (Ensemble Reference Sheet V2) should be used for this request.
 * Uses rollout percentage for gradual deployment.
 * Deterministic per bookId for consistent behavior.
 */
export function shouldUseErsV2(bookId?: string): boolean {
  const flags = getFeatureFlags();
  if (!flags.useErsV2) return false;
  if (flags.ersV2RolloutPercentage >= 100) return true;
  if (flags.ersV2RolloutPercentage <= 0) return false;
  if (bookId) {
    const bucket = simpleHash(bookId) % 100;
    return bucket < flags.ersV2RolloutPercentage;
  }
  return Math.random() * 100 < flags.ersV2RolloutPercentage;
}

/**
 * Check if Page Prompt V2 (clean inner-page prompts) should be used for this request.
 * Uses rollout percentage for gradual deployment.
 * Deterministic per bookId for consistent behavior.
 */
export function shouldUsePagePromptV2(bookId?: string): boolean {
  const flags = getFeatureFlags();
  if (!flags.usePagePromptV2) return false;
  if (flags.pagePromptV2RolloutPercentage >= 100) return true;
  if (flags.pagePromptV2RolloutPercentage <= 0) return false;
  if (bookId) return simpleHash(bookId) % 100 < flags.pagePromptV2RolloutPercentage;
  return Math.random() * 100 < flags.pagePromptV2RolloutPercentage;
}

/**
 * Two-stage inner-page pipeline. Binary on/off (NO rollout percentage by design).
 */
export function shouldUseTwoStageImage(): boolean {
  return getFeatureFlags().useTwoStageImage;
}

/**
 * Two-stage COVER pipeline. Binary on/off (NO rollout percentage by design),
 * independent of the inner-page two-stage flag.
 */
export function shouldUseTwoStageCover(): boolean {
  return getFeatureFlags().useTwoStageCover;
}

/**
 * Whether expensive per-page vision QA (age/identity) runs for measurement.
 */
export function twoStageMeasureEnabled(): boolean {
  return getFeatureFlags().twoStageMeasure;
}

/**
 * Simple hash function for consistent bucketing
 */
export function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Log feature flag decision for debugging
 */
export function logFeatureFlagDecision(
  bookId: string,
  usePipelineLab: boolean,
  reason: string
): void {
  const flags = getFeatureFlags();

  console.log('🚩 [FEATURE-FLAG] Pipeline Lab Decision:');
  console.log(`   Book ID: ${bookId}`);
  console.log(`   Use Pipeline Lab: ${usePipelineLab ? 'YES ✅' : 'NO ❌'}`);
  console.log(`   Reason: ${reason}`);
  console.log(`   Flag enabled: ${flags.usePipelineLabIntegration}`);
  console.log(`   Rollout %: ${flags.pipelineLabRolloutPercentage}%`);
  console.log(`   QA enabled: ${flags.enableQAValidation}`);
}

/**
 * Get environment variable configuration instructions
 */
export function getConfigurationInstructions(): string {
  return `
Feature Flag Configuration:
--------------------------

Add these to your .env file to control feature flags:

## Pipeline Lab Integration
# Enable/disable Pipeline Lab integration entirely
USE_PIPELINE_LAB=false

# Rollout percentage (0-100)
# 0 = disabled, 100 = all books, 10 = 10% of books
PIPELINE_LAB_ROLLOUT_PERCENTAGE=0

## Simple Image Generation (face-matching-first prompts)
# Enable/disable simplified image generation
USE_SIMPLE_IMAGE_GENERATION=false

# Rollout percentage (0-100)
SIMPLE_IMAGE_GENERATION_ROLLOUT_PERCENTAGE=0

## Alternate Story LLM Experiment (text generation)
# Enable/disable routing story-text generation through an alternate,
# OpenAI-compatible provider (e.g. OpenRouter/Gemma) (default OFF)
USE_ALT_STORY_LLM=false

# Rollout percentage (0-100). Deterministic per bookId.
ALT_STORY_LLM_ROLLOUT_PERCENTAGE=0

# Alternate provider config (any OpenAI-compatible endpoint)
STORY_LLM_BASE_URL=https://openrouter.ai/api/v1
STORY_LLM_API_KEY=your_openrouter_key_here
STORY_LLM_MODEL=google/gemma-4-31b-it:free
# Optional: output cap (default 16384) and temperature (default 0.7)
STORY_LLM_MAX_TOKENS=16384
STORY_LLM_TEMPERATURE=0.7
# Optional: only for thinking-capable models (leave unset for Gemma)
# STORY_LLM_REASONING_EFFORT=none

## QA Settings
# Enable/disable QA validation
ENABLE_QA_VALIDATION=true

# Max QA refinement attempts
MAX_QA_ATTEMPTS=2

## Image Provider (gpt-image-2 vs Gemini for the whole book pipeline)
# 'gemini' (default) or 'gpt-image-2'. Prod is untouched until this is flipped.
IMAGE_PROVIDER=gemini

# Rollout percentage (0-100, default 100). When IMAGE_PROVIDER=gpt-image-2 this is a THROTTLE-DOWN
# lever - enabled means 100% unless you lower it. Deterministic per bookId.
IMAGE_PROVIDER_ROLLOUT_PERCENTAGE=100

# gpt-image-2 quality: low | medium (default, validated) | high
GPT_IMAGE_QUALITY=medium

## Two-Stage Cover (gpt-image-2 real-face anchor -> nano restyle)
# Binary on/off, NO rollout %. Default OFF (prod cover unchanged until flipped).
# Set in BOTH dev and prod .env. Fixes multi-character covers rendering
# differently-aged children at the same height/face.
USE_TWOSTAGE_COVER=false

Example gradual rollout for Simple Image Generation:
1. Testing: USE_SIMPLE_IMAGE_GENERATION=true, SIMPLE_IMAGE_GENERATION_ROLLOUT_PERCENTAGE=0 (manual testing only)
2. Stage 1: SIMPLE_IMAGE_GENERATION_ROLLOUT_PERCENTAGE=10 (10% of books)
3. Stage 2: SIMPLE_IMAGE_GENERATION_ROLLOUT_PERCENTAGE=50 (50% of books)
4. Stage 3: SIMPLE_IMAGE_GENERATION_ROLLOUT_PERCENTAGE=100 (all books)
`;
}

/**
 * Whether subscriptions can be purchased. Kill switch, defaults OFF.
 * Gates the Stripe subscription checkout route and the subscribe/subscriptions UI.
 */
export function subscriptionsEnabled(): boolean {
  return getFeatureFlags().subscriptionsEnabled;
}

/**
 * Whether merchandise (cover-art products) is offered. Kill switch, defaults OFF.
 * Gates the merch checkout API, the MCP merch tools and every merch upsell in the UI.
 */
export function merchEnabled(): boolean {
  return getFeatureFlags().merchEnabled;
}

/**
 * Whether the 8x8 hardcover can be bought. Kill switch, defaults OFF.
 */
export function hardcoverEnabled(): boolean {
  return getFeatureFlags().hardcoverEnabled;
}

/**
 * Whether extra copies / multi-item orders can be bought. Kill switch, defaults OFF.
 */
export function extraCopiesEnabled(): boolean {
  return getFeatureFlags().extraCopiesEnabled;
}

/**
 * Whether comics can be created. Kill switch, defaults OFF.
 */
export function comicsEnabled(): boolean {
  return getFeatureFlags().comicsEnabled;
}

/**
 * Whether comics can be ordered in print (A5 saddle stitch) and with the print-ready add-on. Kill switch, defaults OFF.
 */
export function comicPrintEnabled(): boolean {
  return getFeatureFlags().comicPrintEnabled;
}

/** Whether NEW 24-page square comics can be created (COMIC_SQUARE_ENABLED). Kill switch, defaults OFF. */
export function comicSquareEnabled(): boolean {
  return getFeatureFlags().comicSquareEnabled;
}

/** Whether 8/16-page comics get the fast preview (pitch + cover; COMIC_FAST_PREVIEW). Defaults OFF; 24 pages are always fast. */
export function comicFastPreviewEnabled(): boolean {
  return getFeatureFlags().comicFastPreview;
}

/** Whether NEW comic books are stamped for storyboarding V2 (COMIC_STORYBOARD_V2). Defaults OFF; later steps read the stamp. */
export function comicStoryboardV2Enabled(): boolean {
  return getFeatureFlags().comicStoryboardV2;
}

/** Whether each comic page gets a likeness check (and at most one redraw for clear mismatches). Defaults ON. */
export function comicPageQaEnabled(): boolean {
  return getFeatureFlags().comicPageQa;
}

/** Whether the comic preview cover is checked against the costume text and the registry reconciled to it. Defaults ON. */
export function comicCoverCanonEnabled(): boolean {
  return getFeatureFlags().comicCoverCanon;
}

/** Whether new comic books are lettered by code rather than by the image model. Defaults OFF. */
export function comicCodeLetteringEnabled(): boolean {
  return getFeatureFlags().comicCodeLettering;
}

/** Whether comic pages and the cover get the lettering check-and-edit step. Defaults ON. */
export function comicLetteringFixupEnabled(): boolean {
  return getFeatureFlags().comicLetteringFixup;
}

/** Image provider for comic covers and pages (COMIC_IMAGE_PROVIDER): 'flare' (default) | 'gpt-image-2' | 'gemini'. */
export function comicImageProvider(): ImageProvider {
  const v = process.env.COMIC_IMAGE_PROVIDER;
  return v === 'gpt-image-2' || v === 'gemini' ? v : 'flare';
}

/**
 * What a comic draw does when flare fails for good (COMIC_FLARE_FALLBACK): 'gemini' (default, today's Gemini/Qwen fallback)
 * | 'none' (throw; the engine treats the page as failed and redraws it later, so no face reference reaches Gemini).
 */
export function comicFlareFallback(): 'gemini' | 'none' {
  return process.env.COMIC_FLARE_FALLBACK === 'none' ? 'none' : 'gemini';
}

/**
 * Flare quality for comic INNER pages (COMIC_FLARE_QUALITY): 'medium' (default) | 'low' | 'high'; never 'auto'.
 * Low lost likeness and sometimes ignored the panel layout (2026-10-24 experiment); pages are drawn only after purchase.
 */
export function comicFlareQuality(): 'low' | 'medium' | 'high' {
  const v = process.env.COMIC_FLARE_QUALITY;
  return v === 'low' || v === 'high' ? v : 'medium';
}

/**
 * Flare quality for the comic COVER (COMIC_COVER_FLARE_QUALITY): 'high' (default) | 'medium' | 'low'.
 * The cover is the buyer's first look and the model sheet every page copies, so likeness matters most here.
 */
export function comicCoverFlareQuality(): 'low' | 'medium' | 'high' {
  const v = process.env.COMIC_COVER_FLARE_QUALITY;
  return v === 'low' || v === 'medium' ? v : 'high';
}

/**
 * How many interior pages to generate as a pre-purchase preview. 0 (default) = feature off.
 * Staged rollout: set PREVIEW_INTERIOR_PAGES=2 in the droplet .env and restart app + book-worker.
 */
export function previewInteriorPageCount(): number {
  const n = Number.parseInt(process.env.PREVIEW_INTERIOR_PAGES ?? '0', 10);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, 4);
}

/**
 * Whether the legacy storage_cleanup_tracking worker may delete files. DEFAULT OFF (kill switch).
 * That worker deletes book pages, covers and PDFs 30 days after they are written, so it must not
 * run unless explicitly enabled. Photo-retention sweeps are independent of this flag.
 * Mirrored in lib/workers/storageCleanupWorker.js (plain node, cannot import this module).
 */
export function legacyFileCleanupEnabled(): boolean {
  return process.env.LEGACY_FILE_CLEANUP_ENABLED === 'true';
}

/**
 * Book file retention sweep (services/book-file-retention.ts): unpaid previews deleted after 30 days without
 * activity (owner emailed first), paid books' files 6 months after delivery. Off unless explicitly "true".
 */
export function bookFileRetentionEnabled(): boolean {
  return process.env.BOOK_FILE_RETENTION_ENABLED === 'true';
}

/** Dry run (compute + log only, no emails, no deletions) unless BOOK_FILE_RETENTION_DRY_RUN is exactly "false". */
export function bookFileRetentionDryRun(): boolean {
  return process.env.BOOK_FILE_RETENTION_DRY_RUN !== 'false';
}

/** Delete unpaid (and, with the paid warning on, paid) books whose owner has no email to warn. Default true. */
export function bookFileRetentionDeleteWithoutEmail(): boolean {
  return process.env.BOOK_FILE_RETENTION_DELETE_WITHOUT_EMAIL !== 'false';
}

/** Warn paid books' owners 14 days before the 6-month file deletion. Default true (owner decision 2026-10-10); set to "false" to turn it off. */
export function bookFileRetentionWarnPaid(): boolean {
  return process.env.BOOK_FILE_RETENTION_WARN_PAID !== 'false';
}

/** True when the redo guarantee copy and redo links are live (REDO_GUARANTEE_ENABLED=true). Default off. */
export function redoGuaranteeEnabled(): boolean {
  return getFeatureFlags().redoGuaranteeEnabled;
}

/**
 * Whether new picture books are drawn by the comic engine (picture-book mode). Kill switch, defaults OFF.
 */
export function pictureBookComicEngineEnabled(): boolean {
  return getFeatureFlags().pictureBookComicEngine;
}
